import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ServicesPage() {
  const services = [
    {
      category: 'Bridal & Formal Wear',
      icon: 'ri-heart-line',
      items: [
        {
          name: 'Wedding Dress Alterations',
          description: 'Complete bridal gown alterations including bustle creation, train adjustments, bodice fitting, and beadwork modifications. We ensure your dress fits perfectly for your special day.',
          features: ['Custom fitting', 'Bustle creation', 'Train adjustments', 'Beadwork & embellishments'],
        },
        {
          name: 'Bridesmaid Dress Alterations',
          description: 'Professional alterations for bridesmaid dresses to ensure a cohesive and elegant bridal party appearance.',
          features: ['Hem adjustments', 'Waist alterations', 'Strap modifications', 'Neckline adjustments'],
        },
        {
          name: 'Prom & Evening Gown Alterations',
          description: 'Expert alterations for formal evening wear, ensuring you look stunning at any special event.',
          features: ['Perfect fit', 'Length adjustments', 'Bodice alterations', 'Strap customization'],
        },
      ],
    },
    {
      category: 'Men\'s Tailoring',
      icon: 'ri-user-line',
      items: [
        {
          name: 'Suit Tailoring',
          description: 'Professional suit alterations for the perfect fit. From jacket shoulders to trouser breaks, we handle every detail with precision.',
          features: ['Jacket alterations', 'Trouser hemming', 'Waist adjustments', 'Sleeve length'],
        },
        {
          name: 'Tuxedo Alterations',
          description: 'Specialized tuxedo fitting services for weddings, galas, and formal events.',
          features: ['Perfect fit guarantee', 'Satin detail work', 'Cummerbund adjustments', 'Quick turnaround'],
        },
        {
          name: 'Dress Shirt Alterations',
          description: 'Custom fitting for dress shirts including collar, cuff, and body adjustments.',
          features: ['Collar adjustments', 'Cuff modifications', 'Dart additions', 'Length alterations'],
        },
      ],
    },
    {
      category: 'Women\'s Alterations',
      icon: 'ri-shirt-line',
      items: [
        {
          name: 'Dress Alterations',
          description: 'Complete dress alteration services for all styles, from casual to formal wear.',
          features: ['Hem adjustments', 'Waist alterations', 'Shoulder modifications', 'Zipper replacement'],
        },
        {
          name: 'Pants & Skirt Alterations',
          description: 'Professional alterations for pants and skirts to achieve the perfect fit and length.',
          features: ['Hemming', 'Waist adjustments', 'Tapering', 'Zipper repair'],
        },
        {
          name: 'Blouse & Top Alterations',
          description: 'Expert alterations for blouses, tops, and shirts to enhance fit and comfort.',
          features: ['Shoulder adjustments', 'Sleeve modifications', 'Dart additions', 'Length changes'],
        },
      ],
    },
    {
      category: 'Specialty Services',
      icon: 'ri-scissors-line',
      items: [
        {
          name: 'Leather & Suede Alterations',
          description: 'Specialized alterations for leather jackets, suede garments, and other delicate materials.',
          features: ['Expert handling', 'Zipper replacement', 'Lining repair', 'Resizing'],
        },
        {
          name: 'Zipper Replacement',
          description: 'Quick and reliable zipper repairs and replacements for all types of garments and accessories.',
          features: ['All zipper types', 'Same-day service available', 'Invisible zippers', 'Heavy-duty zippers'],
        },
        {
          name: 'Custom Fitting',
          description: 'Personalized fitting consultations to transform your garments into perfectly tailored pieces.',
          features: ['One-on-one consultation', 'Multiple fittings', 'Style recommendations', 'Precision measurements'],
        },
        {
          name: 'Garment Resizing',
          description: 'Complete resizing services to make your favorite pieces fit like new again.',
          features: ['Size up or down', 'Maintain original style', 'Quality guarantee', 'All garment types'],
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center bg-gradient-to-r from-amber-700 to-amber-900">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://public.readdy.ai/ai/img_res/05c8e9b0f823f6aa0ae8e86e370b6595.jpg"
            alt="Our Services"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
            Mobile Alteration Services
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Professional tailoring delivered to your doorstep. Experience expert alterations in the comfort of your home.
          </p>
        </div>
      </section>

      {/* Services Sections */}
      {services.map((category, categoryIndex) => (
        <section
          key={categoryIndex}
          className={`py-20 ${categoryIndex % 2 === 0 ? 'bg-white' : 'bg-amber-50'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-16 flex items-center justify-center bg-amber-700 rounded-full mx-auto mb-4">
                <i className={`${category.icon} text-3xl text-white`}></i>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '\"Playfair Display\", serif' }}>
                {category.category}
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {category.items.map((service, serviceIndex) => (
                <div
                  key={serviceIndex}
                  className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow border border-gray-100"
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.name}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <div className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center">
                        <i className="ri-check-line text-amber-700 mr-2"></i>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Process Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: '\"Playfair Display\", serif' }}>
              Our Process
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Simple, efficient, and designed around your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-700 rounded-full mx-auto mb-4 text-3xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold mb-3">Consultation</h3>
              <p className="text-gray-300">Bring in your garment and discuss your alteration needs with our experts.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-700 rounded-full mx-auto mb-4 text-3xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold mb-3">Measurement</h3>
              <p className="text-gray-300">Precise measurements and pinning to ensure perfect fit and style.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-700 rounded-full mx-auto mb-4 text-3xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold mb-3">Alteration</h3>
              <p className="text-gray-300">Our skilled seamstresses work their magic with attention to every detail.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-700 rounded-full mx-auto mb-4 text-3xl font-bold">
                4
              </div>
              <h3 className="text-xl font-bold mb-3">Final Fitting</h3>
              <p className="text-gray-300">Try on your garment and ensure you're completely satisfied with the results.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-700 to-amber-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: '\"Playfair Display\", serif' }}>
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8">
            Book your consultation today and let us bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-amber-900 px-8 py-4 rounded-md text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Book Consultation
            </Link>
            <Link
              to="/pricelist"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-md text-lg font-semibold hover:bg-white hover:text-amber-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
